import React, { useState } from "react";
import {
  IconAlertTriangle,
  IconPlus,
  IconSearch,
  IconTool,
  IconCheck,
  IconEye,
  IconFilter,
  IconX,
  IconFlame,
  IconBolt,
  IconDroplet,
  IconExclamationMark
} from "@tabler/icons-react";
import {
  useDamageReports,
  useAssets,
  createDamageReport,
  updateDamageReport,
  recordMaintenance
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetDamageTab() {
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const { data: reports, error, isLoading, mutate } = useDamageReports();
  const { data: assets } = useAssets();

  const [formData, setFormData] = useState({
    asset_id: "",
    incident_date: new Date().toISOString().split("T")[0],
    reported_by: "",
    damage_type: "Physical Damage",
    severity: "Moderate",
    description: "",
    suspected_cause: "",
    operational_impact: "Room/Station temporarily offline",
    safety_risk: "None",
    immediate_action: "Powered off and unplugged",
    is_safe_to_use: 0,
    recommended_action: "Repair by Internal Technician",
    estimated_repair_cost: ""
  });

  const filteredReports = Array.isArray(reports)
    ? reports.filter((r) => {
        if (severityFilter !== "all" && r.severity !== severityFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          r.report_number?.toLowerCase().includes(q) ||
          r.asset_code?.toLowerCase().includes(q) ||
          r.asset_name?.toLowerCase().includes(q) ||
          r.damage_type?.toLowerCase().includes(q) ||
          r.reported_by?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenReportModal = () => {
    setFormData({
      asset_id: assets?.[0]?.id || "",
      incident_date: new Date().toISOString().split("T")[0],
      reported_by: "",
      damage_type: "Physical Damage",
      severity: "Moderate",
      description: "",
      suspected_cause: "",
      operational_impact: "Equipment temporarily unavailable",
      safety_risk: "None",
      immediate_action: "Isolated and marked out of service",
      is_safe_to_use: 0,
      recommended_action: "Internal Inspection / Repair",
      estimated_repair_cost: ""
    });
    setIsReportModalOpen(true);
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an affected asset");
      return;
    }
    if (!formData.reported_by) {
      toast.error("Please state who is reporting the damage");
      return;
    }
    if (!formData.description) {
      toast.error("Please provide damage description");
      return;
    }

    try {
      await createDamageReport(formData);
      toast.success("Damage incident registered successfully");
      setIsReportModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to submit damage report");
    }
  };

  const handleStatusUpdate = async (reportId, nextStatus) => {
    try {
      await updateDamageReport(reportId, { status: nextStatus });
      toast.success(`Damage report updated to: ${nextStatus}`);
      mutate();
      if (selectedReport?.id === reportId) {
        setSelectedReport({ ...selectedReport, status: nextStatus });
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleCreateWorkOrderFromDamage = async (report) => {
    try {
      await recordMaintenance({
        asset_id: report.asset_id,
        work_order_type: "Corrective / Breakdown Repair",
        priority: report.severity === "Critical" ? "Critical" : "High",
        issue_description: `Damage Report #${report.report_number}: ${report.description} (${report.damage_type})`,
        service_provider_type: "Internal Technician",
        assigned_technician: "Maintenance Lead",
        estimated_cost: report.estimated_repair_cost || 0,
        start_date: new Date().toISOString().split("T")[0]
      });
      await updateDamageReport(report.id, { status: "Under Repair" });
      toast.success("Maintenance Work Order created automatically!");
      mutate();
      setIsDetailModalOpen(false);
    } catch (err) {
      toast.error("Failed to generate work order");
    }
  };

  const getSeverityBadge = (sev) => {
    switch (sev) {
      case "Critical":
        return "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300 dark:border-rose-800";
      case "Major":
        return "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border-orange-300 dark:border-orange-800";
      case "Moderate":
        return "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300 dark:border-amber-800";
      default:
        return "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-300 dark:border-blue-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconAlertTriangle className="w-6 h-6 text-rose-500" />
            Equipment Damage & Incident Reports
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Register asset breakdowns, safety hazards, and physical damages with 1-click maintenance dispatch.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports or assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-restro-green w-56 text-gray-800 dark:text-gray-200"
            />
          </div>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
          >
            <option value="all">All Severities</option>
            <option value="Critical">Critical</option>
            <option value="Major">Major</option>
            <option value="Moderate">Moderate</option>
            <option value="Minor">Minor</option>
          </select>

          <button
            onClick={handleOpenReportModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            Report Damage
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Report #</th>
                <th className="px-6 py-4">Asset</th>
                <th className="px-6 py-4">Damage Type</th>
                <th className="px-6 py-4">Severity</th>
                <th className="px-6 py-4">Safe To Use?</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-400">
                    Loading damage reports...
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400">
                    <IconAlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-30 text-rose-500" />
                    No equipment damage reports found.
                  </td>
                </tr>
              ) : (
                filteredReports.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                    <td className="px-6 py-4">
                      <span className="font-mono text-xs font-bold text-gray-900 dark:text-white">
                        {item.report_number || `DMR-${item.id}`}
                      </span>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {item.incident_date ? new Date(item.incident_date).toLocaleDateString() : "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {item.asset_name || "Asset #" + item.asset_id}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        {item.asset_code || `AST-${item.asset_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {item.damage_type || "General Damage"}
                      </span>
                      <div className="text-xs text-gray-400 truncate max-w-xs">{item.description}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${getSeverityBadge(
                          item.severity
                        )}`}
                      >
                        {item.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {item.is_safe_to_use ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          <IconCheck className="w-3.5 h-3.5" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 dark:text-rose-400">
                          <IconAlertTriangle className="w-3.5 h-3.5" /> NO - Do Not Use
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                        {item.status || "Reported"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => {
                          setSelectedReport(item);
                          setIsDetailModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition"
                      >
                        <IconEye className="w-3.5 h-3.5" />
                        Inspect
                      </button>
                      {item.status !== "Closed" && item.status !== "Under Repair" && (
                        <button
                          onClick={() => handleCreateWorkOrderFromDamage(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-lg transition"
                        >
                          <IconTool className="w-3.5 h-3.5" />
                          Create Work Order
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: New Damage Report */}
      {isReportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconAlertTriangle className="w-5 h-5 text-rose-500" />
                Register Asset Damage Incident
              </h3>
              <button
                onClick={() => setIsReportModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateReport} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Select Damaged Asset *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose asset --</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code} - {a.asset_name} ({a.location || a.room_number || "Main Building"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Reported By (Staff) *
                  </label>
                  <input
                    type="text"
                    placeholder="Staff name"
                    value={formData.reported_by}
                    onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Date Occurred *
                  </label>
                  <input
                    type="date"
                    value={formData.incident_date}
                    onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Damage Type *
                  </label>
                  <select
                    value={formData.damage_type}
                    onChange={(e) => setFormData({ ...formData, damage_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  >
                    <option value="Physical Damage">Physical Damage / Broken</option>
                    <option value="Electrical Failure">Electrical / Short Circuit</option>
                    <option value="Water Damage">Water / Liquid Ingress</option>
                    <option value="Wear and Tear">Extreme Wear & Tear</option>
                    <option value="Accidental Damage">Accidental Drop / Impact</option>
                    <option value="Fire Damage">Fire / Overheating</option>
                    <option value="Component Burnout">Motor / Component Burnout</option>
                    <option value="Software/Firmware Crash">Electronic / Firmware Fault</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Damage Severity *
                  </label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-semibold"
                  >
                    <option value="Minor">Minor - Operable with cosmetic flaw</option>
                    <option value="Moderate">Moderate - Degraded performance</option>
                    <option value="Major">Major - Completely inoperable</option>
                    <option value="Critical">Critical - Safety risk / fire hazard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Is Asset Still Safe to Use?
                  </label>
                  <select
                    value={formData.is_safe_to_use}
                    onChange={(e) => setFormData({ ...formData, is_safe_to_use: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-semibold"
                  >
                    <option value={0}>NO - Unsafe, Halt Operation Immediately</option>
                    <option value={1}>YES - Safe for restricted temporary use</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Damage Description & Symptoms *
                </label>
                <textarea
                  rows="2"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of visible cracks, noises, burns, or failure modes..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Suspected Root Cause
                  </label>
                  <input
                    type="text"
                    value={formData.suspected_cause}
                    onChange={(e) => setFormData({ ...formData, suspected_cause: e.target.value })}
                    placeholder="e.g. Power surge or guest accidental drop"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Estimated Repair Cost (NPR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.estimated_repair_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_repair_cost: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsReportModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-sm transition"
                >
                  Submit Incident Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Inspection & Details */}
      {isDetailModalOpen && selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Incident #{selectedReport.report_number || selectedReport.id}
                </h3>
                <div className="text-xs text-gray-400">
                  Reported on {new Date(selectedReport.incident_date).toLocaleDateString()} by {selectedReport.reported_by}
                </div>
              </div>
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {selectedReport.asset_name}
                    </div>
                    <div className="text-xs font-mono text-gray-400">{selectedReport.asset_code}</div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold border ${getSeverityBadge(
                      selectedReport.severity
                    )}`}
                  >
                    {selectedReport.severity} Severity
                  </span>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs space-y-1.5 text-gray-600 dark:text-gray-300">
                  <div>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">Type:</span> {selectedReport.damage_type}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">Description:</span> {selectedReport.description}
                  </div>
                  {selectedReport.suspected_cause && (
                    <div>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">Suspected Cause:</span> {selectedReport.suspected_cause}
                    </div>
                  )}
                  {selectedReport.immediate_action && (
                    <div>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">Immediate Action:</span> {selectedReport.immediate_action}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Workflow Status:</span>
                <div className="flex gap-2">
                  {["Under Inspection", "Repair Approved", "Closed"].map((st) => (
                    <button
                      key={st}
                      onClick={() => handleStatusUpdate(selectedReport.id, st)}
                      className={`px-2.5 py-1 text-xs rounded-lg font-medium transition ${
                        selectedReport.status === st
                          ? "bg-restro-green text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsDetailModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handleCreateWorkOrderFromDamage(selectedReport)}
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition flex items-center gap-2"
                >
                  <IconTool className="w-4 h-4" />
                  Dispatch Work Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
